import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Plus, SquarePen, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import api from "../../axios/api";
import { useDispatch } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CustomButton from "../../componentsII/Buttons";
import ConfirmationModal from "../../componentsII/ConfirmationModal";
import { useSelector } from "react-redux";

const vehicleDataTypes = [
  {
    id: 1,
    value: "DISTANCE_BASE",
    label: "DISTANCE",
  },
  {
    id: 2,
    value: "FUEL_BASE",
    label: "FUEL",
  },
];

const CompanyVehicleDetailModel = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [activeTab, setActiveTab] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [makesRes, vehicleTypesRes] = await Promise.all([
          api.get("/vehicle/vehiclemake"),
          api.get("/vehicle/vehicleType"),
        ]);
        const yearsRes = await api.get(`/company/companydatayear?company_id=${user.company.id}`);
        const formattedYears = yearsRes.data.data.map((year) => ({
          label: year.start_year,
          value: year.id,
        }));
        setYears(formattedYears);
        setMakes(makesRes.data.data);
        setVehicleTypes(vehicleTypesRes.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    handleMakeChange(detailModalData?.make_details?.make_id);
    handleTypeChange(detailModalData?.veh_type_details?.veh_type_id);
  }, []);

  const handleMakeChange = async (makeId) => {
    if (makeId) {
      try {
        const res = await api.get(
          `/vehicle/vehiclemodel?vehicle_make_id=${makeId}`
        );
        setModels(res.data.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    }
  };

  const handleTypeChange = async (typeId) => {
    if (typeId) {
      try {
        const res = await api.get(
          `/vehicle/vehiclesubtype?vehicle_type_id=${typeId}`
        );
        setVehicleSubtypes(res.data.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    }
  };

  const schema = Yup.object().shape({
    veh_name_code: Yup.string().required("Vehicle name code is required"),
    year: Yup.number().required("Year is required"),
    make_id: Yup.number().required("Make is required"),
    model_id: Yup.number().required("Model is required"),
    veh_datatype_available: Yup.string().required(
      "Vehicle data type is required"
    ),
    veh_type_id: Yup.number().required("Vehicle type is required"),
    veh_subtype_id: Yup.number().required("Vehicle subtype is required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData?.value,
      veh_name_code: detailModalData?.label,
      company_id: detailModalData?.company_id,
      comp_data_year_id: detailModalData?.comp_data_year_id,
      year: detailModalData?.year,
      make_id: detailModalData?.make_details?.make_id,
      model_id: detailModalData?.model_details?.model_id,
      veh_datatype_available: detailModalData?.veh_datatype_available,
      veh_type_id: detailModalData?.veh_type_details?.veh_type_id,
      veh_subtype_id: detailModalData?.veh_subtype_details?.veh_subtype_id,
      comp_location_id: detailModalData?.comp_location_id,
    },
  });

  const handleEdit = () => {
    setIsEditable(true);
  };
  const handleEditSubmit = async (data) => {
    const body = { ...data };
    try {
      const response = await api.post("/vehicle/addUpdateCompanyVehicle", body);
      if (response.data.status === "200") {
        showNotification(response.data.message, "success");
        setIsEditable(false);

        const make_detail = makes?.find((item) => item.value == data?.make_id);
        const model_detail = models?.find(
          (item) => item.value == data?.model_id
        );
        const type_detail = vehicleTypes?.find(
          (item) => item.value == data?.veh_type_id
        );

        const sub_type_detail = vehicleSubtypes?.find(
          (item) => item.value == data?.veh_subtype_id
        );
        setDetailModalData((prev) => ({
          ...prev,
          label: data?.veh_name_code,
          year: data?.year,
          make_details: {
            make_id: make_detail?.value,
            make_name: make_detail?.label,
          },
          model_details: {
            model_id: model_detail?.value,
            model_name: model_detail?.label,
          },
          veh_type_details: {
            veh_type_id: type_detail?.value,
            type_name: type_detail?.label,
          },
          veh_subtype_details: {
            veh_subtype_id: sub_type_detail?.value,
            sub_type_name: sub_type_detail?.label,
          },
          veh_datatype_available: data?.veh_datatype_available,
        }));
        refetch();
      } else {
        showNotification(response.data.message, "error");
        setIsEditable(false);
      }
    } catch (error) {
      showNotification(error.response.data.message, "error");
      console.error("Error submitting form:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/vehicle/deleteCompanyVehicle/${detailModalData?.value}`
      );
      if (response.data.status == "200") {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        console.log(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="h-full">
      <div>
        <div className="flex border-b border-dashed border-gray-500 ">
          <div className="w-[30%] border-r border-gray-500">
            <img src={detailModalData?.car} />
          </div>
          <div className="px-8">
            <h1 className="text-2xl font-bold text-green-600">
              {detailModalData?.label}
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium">
              {detailModalData?.make_details?.make_name} -{" "}
              {detailModalData?.model_details.model_name}
            </p>
            <div className="flex">
              <button></button>
              <button onClick={() => setActiveTab(1)}></button>
              <ButtonGroup
                variant=""
                aria-label="Basic button group"
                style={{ border: "2px solid #388E3C" }}
              >
                <Button
                  style={{
                    backgroundColor: activeTab === 0 ? "#388E3C" : "",
                    color: activeTab === 0 ? "#fff" : "",
                  }}
                  color="green"
                  onClick={() => setActiveTab(0)}
                >
                  Details
                </Button>
                <Button
                  style={{
                    backgroundColor: activeTab === 1 ? "#388E3C" : "",
                    color: activeTab === 1 ? "#fff" : "",
                  }}
                  color="gray"
                  onClick={() => setActiveTab(1)}
                >
                  Usage data
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
        <div>
          {activeTab == 0 ? (
            <div className="px-6">
              <div className="flex justify-between w-full items-center">
                <div className="text-gray-800 my-6 text-xl font-bold">
                  Vehicle Details
                </div>
                {!isEditable ? (
                  <div className="cursor-pointer" onClick={handleEdit}>
                    <SquarePen color="#16a34a" />
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <form onSubmit={handleSubmit(handleEditSubmit)}>
                <CompInput
                  title="Name"
                  value={detailModalData?.label}
                  name="veh_name_code"
                  isError={!!errors.veh_name_code}
                  errorText={errors.veh_name_code?.message}
                  control={control}
                  isEditable={isEditable}
                />
                <CompSelect2
                  title="Year"
                  value={years.find((item) => item?.value == detailModalData?.year)?.label}
                  name="year"
                  isError={!!errors.year}
                  errorText={errors.year?.message}
                  control={control}
                  isEditable={isEditable}
                  options={years}
                />
                <CompSelect
                  title="Make"
                  value={detailModalData?.make_details?.make_name}
                  name="make_id"
                  isError={!!errors.make_id}
                  errorText={errors.make_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={makes}
                  handleChange={(value, field) => {
                    handleMakeChange(value)
                    setValue('model_id', undefined)
                  }}
                />
                <CompSelect
                  title="Model"
                  value={detailModalData?.model_details.model_name}
                  name="model_id"
                  isError={!!errors.model_id}
                  errorText={errors.model_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={models}
                />
                <CompSelect
                  title="Vehicle Type"
                  value={detailModalData?.veh_type_details?.type_name}
                  name="veh_type_id"
                  isError={!!errors.veh_type_id}
                  errorText={errors.veh_type_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={vehicleTypes}
                  handleChange={(value, field) => {
                    handleTypeChange(value)
                    setValue('veh_subtype_id', undefined)
                  }}
                />
                <CompSelect
                  title="Vehicle Sub Type"
                  value={detailModalData?.veh_subtype_details?.sub_type_name}
                  name="veh_subtype_id"
                  isError={!!errors.veh_subtype_id}
                  errorText={errors.veh_subtype_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={vehicleSubtypes}
                />
                <CompSelect
                  title="Vehicle Usage Data (Fuel/Distance)"
                  value={
                    vehicleDataTypes?.find(
                      (item) =>
                        item.value == detailModalData?.veh_datatype_available
                    ).label
                  }
                  name="veh_datatype_available"
                  isError={!!errors.veh_datatype_available}
                  errorText={errors.veh_datatype_available?.message}
                  control={control}
                  isEditable={isEditable}
                  options={vehicleDataTypes}
                  isBorderBottom={false}
                />
                <div className="w-full flex justify-end gap-5 items-center py-5">
                  {isEditable ? (
                    <>
                      <CustomButton
                        className="bg-red-500 hover:bg-red-700 text-white "
                        click={() => {
                          handleTypeChange(
                            detailModalData?.veh_type_details?.veh_type_id
                          );
                          handleMakeChange(
                            detailModalData?.make_details?.make_id
                          );
                          setValue('model_id', detailModalData?.model_details?.model_id)
                          setValue('make_id', detailModalData?.make_details?.model_id)
                          reset();
                          setIsEditable(false);
                        }}
                        text="Cancel"
                      />
                      <CustomButton
                        className="bg-green-600 hover:bg-green-700 text-white "
                        type="submit"
                        text="Submit"
                      />
                    </>
                  ) : (
                    <div className="flex justify-center w-full">
                      <CustomButton
                        className="bg-red-500 hover:bg-red-700 text-white "
                        click={() => setIsDeleteModalOpen(true)}
                        text="Delete"
                      />
                    </div>
                  )}
                </div>
              </form>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-2 pt-6">
                <div>
                  <div className="flex gap-2 items-center">
                    <p className="font-medium text-[20px]">{
  detailModalData?.distance_consumption?.co2_emission_tones ??
  detailModalData?.fuel_consumption?.co2_emission_tones ??
  "--"
}
</p>
                    <p>
                      tCO<sub>2</sub>e
                    </p>
                  </div>
                  <p>Total emiision for this vehicle</p>
                </div>
                <div>
                  <div className="flex gap-2 item-center">
                    <p className="font-medium text-[20px]">{
  detailModalData?.distance_consumption?.distance_qty ??
  detailModalData?.fuel_consumption?.fuel_qty ??
  "--"
}</p>
                    <div className="flex items-center">
                      <p>{
  detailModalData?.distance_consumption?.unit?.unit_label ??
  detailModalData?.fuel_consumption?.unit?.unit_label ??
  "--"
}
</p>
                    </div>
                  </div>
                  <p>Total emiision for this vehicle</p>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-[25px] pt-5">
                <div className="font-medium text-gray-900">
  {detailModalData?.distance_consumption
    ? "Distance covered"
    : detailModalData?.fuel_consumption
    ? "Fuel consumption"
    : "--"}
</div>

                </h3>
                <div
                  className={`grid grid-cols-2 h-16 w-full items-center border-b border-gray-500`}
                >
                  <div className="font-medium text-gray-900">Fuel Type</div>
                  <div className="">
                    <div className="">{
  detailModalData?.distance_consumption?.fuel_name ??
  detailModalData?.fuel_consumption?.fuel_name ??
  "--"
}</div>
                  </div>
                </div>
                <div className={`grid grid-cols-2 h-16 w-full items-center`}>
                <div className="font-medium text-gray-900">
  {detailModalData?.distance_consumption
    ? "Total distance covered"
    : detailModalData?.fuel_consumption
    ? "Total fuel consumed"
    : "--"}
</div>

                  <div className="">
                    <div className="">{
  detailModalData?.distance_consumption?.distance_qty ??
  detailModalData?.fuel_consumption?.fuel_qty ??
  "--"
} {
  detailModalData?.distance_consumption?.unit?.unit_label ??
  detailModalData?.fuel_consumption?.unit?.unit_label ??
  "--"
}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Vehicle</div>}
      />
    </div>
  );
};

export default CompanyVehicleDetailModel;

const CompInput = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  required = true,
}) => {
  return (
    <div
      className={`grid grid-cols-2 h-16 w-full items-center ${isBorderBottom ? "border-b border-gray-500" : ""
        }`}
    >
      <div className="font-medium text-gray-900 flex gap-1 ">{title}<span className={required ? "text-red-500" : "invisible"}>*</span></div>
      <div className="">
        {isEditable ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={isError}
                helperText={errorText}
                margin="none"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    padding: 0,
                    "& .MuiInputBase-input": {
                      padding: "10px",
                    },
                  },
                }}
                sx={{
                  padding: 0,
                  margin: 0,
                }}
              />
            )}
          />
        ) : (
          <div className="">{value}</div>
        )}
      </div>
    </div>
  );
};

const CompSelect = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  options,
  required = true,
  handleChange = () => { },
}) => {
  return (
    <div
      className={`grid grid-cols-2 h-16 w-full items-center ${isBorderBottom ? "border-b border-gray-500" : ""
        }`}
    >
      <div className="font-medium text-gray-900 flex gap-1">{title}<span className={required ? "text-red-500" : "invisible"}>*</span></div>
      <div className="">
        {isEditable ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={isError}>
                <Select
                  {...field}
                  sx={{
                    padding: 0, // Removes padding in the Select component itself
                    "& .MuiInputBase-input": {
                      padding: "10px !important", // Removes padding from the input area
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value, field);
                  }}
                  renderValue={(selected) => {
                    const selectedVal = options.find((item) => item?.value == selected)
                    if (selectedVal == undefined) {
                      return <em>Select option</em>;
                    }
                    return selectedVal?.label;
                  }}
                >
                   {options?.length == 0 ? 
                     <>
                        <MenuItem value={undefined} disabled>
                          Select option
                        </MenuItem>
                      </>
                    : <></>}
                  {options.map((make) => (
                    <MenuItem key={make.value} value={make.value}>
                      {make.label}
                    </MenuItem>
                  ))}
                </Select>
                {errorText && <p className="text-xs text-red-500">{errorText}</p>}
              </FormControl>
            )}
          />
        ) : (
          <div className="">{value}</div>
        )}
      </div>
    </div>
  );
};

const CompSelect2 = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  options,
  required = true,
  handleChange = () => { },
}) => {
  const displayLabel = options.find((option) => option.value === value)?.label || value;

  return (
    <div
      className={`grid grid-cols-2 h-16 w-full items-center ${isBorderBottom ? "border-b border-gray-500" : ""
        }`}
    >
      <div className="font-medium text-gray-900">{title}<span className={required ? "text-red-500" : "invisible"}>*</span></div>
      <div className="">
        {isEditable ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={isError}>
                <Select
                  {...field}
                  sx={{
                    padding: 0, // Removes padding in the Select component itself
                    "& .MuiInputBase-input": {
                      padding: "10px !important", // Removes padding from the input area
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value);
                  }}
                >
                  {options.map((make) => (
                    <MenuItem key={make.value} value={make.value}>
                      {make.label}
                    </MenuItem>
                  ))}
                </Select>
                {errorText && <p>{errorText}</p>}
              </FormControl>
            )}
          />
        ) : (
          <div className="">{displayLabel}</div>
        )}
      </div>
    </div>
  );
};
